import { navigate } from '../../router';
import { loadCart, addToCart, CART_CHANGED_EVENT, type CartView, type CartViewLine } from '../../services/cart';
import { formatPrice } from '../../pages/ProductsPage';
import { curl, ensureMotifDefs } from './frondArt';

// CartDrawer is the storefront cart — a right-side slide-over (a full-height
// sheet on phones, via cart.css) opened from the nav cart button. It renders the
// current cart (signed-in server cart or anonymous device cart, both via
// loadCart), lets the shopper adjust quantities or remove lines, shows the
// subtotal, and sends them to checkout. It is a single document-level singleton;
// call openCartDrawer() to show it.

const MAX_QTY = 99;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function statusLine(text: string): HTMLElement {
  const p = el('p', 'cart-drawer__status', text);
  p.setAttribute('role', 'status');
  return p;
}

class CartDrawer {
  private root: HTMLElement | null = null;
  private body: HTMLElement | null = null;
  private footer: HTMLElement | null = null;
  private lastFocus: HTMLElement | null = null;
  private busy = false;

  private readonly onCartChanged = (): void => {
    void this.render();
  };

  private readonly onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.close();
  };

  open(): void {
    if (this.root) {
      void this.render();
      return;
    }
    this.lastFocus = document.activeElement as HTMLElement | null;
    ensureMotifDefs();

    const root = el('div', 'cart-drawer');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Shopping cart');

    const backdrop = el('div', 'cart-drawer__backdrop');
    backdrop.addEventListener('click', () => this.close());

    const panel = el('div', 'cart-drawer__panel');

    const header = el('div', 'cart-drawer__header');
    const title = el('h2', 'cart-drawer__title', 'Your cart');
    const closeBtn = el('button', 'cart-drawer__close');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close cart');
    closeBtn.append('×'); // ×
    closeBtn.addEventListener('click', () => this.close());
    header.append(title, closeBtn);

    const body = el('div', 'cart-drawer__body');
    const footer = el('div', 'cart-drawer__footer');

    panel.append(header, body, footer);
    root.append(backdrop, panel);
    document.body.appendChild(root);
    document.body.classList.add('cart-open');

    this.root = root;
    this.body = body;
    this.footer = footer;

    window.addEventListener(CART_CHANGED_EVENT, this.onCartChanged);
    document.addEventListener('keydown', this.onKeydown);
    queueMicrotask(() => closeBtn.focus());
    void this.render();
  }

  close(): void {
    if (!this.root) return;
    window.removeEventListener(CART_CHANGED_EVENT, this.onCartChanged);
    document.removeEventListener('keydown', this.onKeydown);
    this.root.remove();
    document.body.classList.remove('cart-open');
    this.root = this.body = this.footer = null;
    this.lastFocus?.focus?.();
    this.lastFocus = null;
  }

  private async render(): Promise<void> {
    const body = this.body;
    const footer = this.footer;
    if (!body || !footer) return;
    body.replaceChildren(statusLine('Loading your cart…'));
    footer.replaceChildren();

    let view: CartView;
    try {
      view = await loadCart();
    } catch {
      if (this.body === body) body.replaceChildren(statusLine('Could not load your cart. Please try again.'));
      return;
    }
    if (this.body !== body) return; // closed (or re-opened) while loading

    if (view.lines.length === 0) {
      body.replaceChildren(this.emptyState());
      footer.replaceChildren();
      return;
    }
    body.replaceChildren(this.lineList(view));
    footer.replaceChildren(this.summary(view));
  }

  private emptyState(): HTMLElement {
    const wrap = el('div', 'cart-drawer__empty');
    wrap.append(el('p', 'cart-drawer__empty-text', 'Your cart is empty.'));
    const browse = el('a', 'buy-link', 'Browse products');
    browse.setAttribute('href', '/products');
    browse.addEventListener('click', (e) => {
      e.preventDefault();
      this.close();
      navigate('/products');
    });
    wrap.append(browse);
    return wrap;
  }

  private lineList(view: CartView): HTMLElement {
    const list = el('ul', 'cart-lines');
    for (const line of view.lines) list.append(this.lineRow(line));
    return list;
  }

  private lineRow(line: CartViewLine): HTMLElement {
    const row = el('li', 'cart-line');

    const info = el('div', 'cart-line__info');
    info.append(el('span', 'cart-line__name', line.name));
    info.append(el('span', 'cart-line__unit', formatPrice(line.priceCents, line.currency) + ' each'));

    const controls = el('div', 'cart-line__controls');
    const stepper = el('div', 'qty-stepper');
    stepper.setAttribute('role', 'group');
    stepper.setAttribute('aria-label', `Quantity for ${line.name}`);

    const dec = el('button', 'qty-stepper__btn');
    dec.type = 'button';
    dec.setAttribute('aria-label', line.quantity <= 1 ? `Remove ${line.name}` : `Decrease ${line.name}`);
    dec.append('−'); // −
    dec.addEventListener('click', () => void this.setQty(line.slug, line.quantity - 1));

    const count = el('span', 'qty-stepper__count', String(line.quantity));
    count.setAttribute('aria-live', 'polite');

    const inc = el('button', 'qty-stepper__btn');
    inc.type = 'button';
    inc.setAttribute('aria-label', `Increase ${line.name}`);
    inc.append('+');
    inc.disabled = line.quantity >= MAX_QTY;
    inc.addEventListener('click', () => void this.setQty(line.slug, line.quantity + 1));

    stepper.append(dec, count, inc);

    const lineTotal = el('span', 'cart-line__total', formatPrice(line.priceCents * line.quantity, line.currency));

    const remove = el('button', 'cart-line__remove');
    remove.type = 'button';
    remove.setAttribute('aria-label', `Remove ${line.name}`);
    remove.append('Remove');
    remove.addEventListener('click', () => void this.setQty(line.slug, 0));

    controls.append(stepper, lineTotal, remove);
    row.append(info, controls);
    return row;
  }

  private summary(view: CartView): HTMLElement {
    const wrap = el('div', 'cart-summary');

    const subtotal = el('div', 'cart-summary__row');
    subtotal.append(el('span', undefined, 'Subtotal'), el('span', 'cart-summary__amount', formatPrice(view.subtotalCents, view.currency)));
    wrap.append(subtotal);
    wrap.append(el('p', 'cart-summary__note', 'Shipping calculated at checkout.'));

    const checkout = el('button', 'buy-btn cart-summary__checkout');
    checkout.type = 'button';
    checkout.append(curl('buy-btn__curl'), document.createTextNode(' Checkout'));
    checkout.addEventListener('click', () => {
      this.close();
      navigate('/checkout');
    });
    wrap.append(checkout);
    return wrap;
  }

  // setQty sets a line to qty (0 removes), via the cart service (server or device
  // cart). The CART_CHANGED event addToCart fires re-renders the drawer, so this
  // just guards against overlapping clicks.
  private async setQty(slug: string, qty: number): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await addToCart(slug, Math.max(0, Math.min(MAX_QTY, qty)));
    } catch {
      if (this.body) this.body.replaceChildren(statusLine('Could not update your cart. Please try again.'));
    } finally {
      this.busy = false;
    }
  }
}

const drawer = new CartDrawer();

// openCartDrawer shows the storefront cart drawer (creating it on first use).
export function openCartDrawer(): void {
  drawer.open();
}
